import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowRight, ChevronLeft, ChevronRight, AlertTriangle, Truck, CheckCircle2,
  Factory, ClipboardList, LayoutGrid, CalendarRange, Search, X,
} from "lucide-react";

const KANBAN_FILTER_KEY = "popcrn.kanban.filters.v1";

/* Einheitliche Sortenfarben (Farbpunkt + Name), Pink nur für Buttons/Navigation */
const SORT_COLORS = [
  [/caramell|karamell/i, "#E08E2B"],
  [/sea ?salt|salz/i, "#5BB5C9"],
  [/cookies|cream|schoko/i, "#8E7CC3"],
  [/biscuit|biscoff/i, "#E6C48A"],
  [/coffee|kaffee/i, "#A0694A"],
  [/tr(ü|u)ffel|truffle/i, "#8FA36B"],
];
const sortColor = (name = "") => (SORT_COLORS.find(([re]) => re.test(name)) || [])[1] || "#9AA0A6";
const nf = (v) => Number(v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const fmtDay = (d) => d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
const START_HOUR = 8;

/* Status → Kanban-Spalte (bestehende Felder, keine Datenänderung) */
const colOf = (o) => {
  if (o.status === "planned") return "planned";
  if (o.status === "in_progress") return "in_progress";
  if (o.status === "completed") return o.picked_at ? "versandbereit" : "fertig";
  return null; // cancelled u.a. ausblenden
};
const COLS = [
  { key: "planned", title: "Geplant", icon: ClipboardList },
  { key: "in_progress", title: "In Produktion", icon: Factory },
  { key: "fertig", title: "Fertig", icon: CheckCircle2 },
  { key: "versandbereit", title: "Versandbereit", icon: Truck },
];
const COL_KEYS = COLS.map((c) => c.key);

export default function ProductionBoard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [cap, setCap] = useState(null);
  const [hints, setHints] = useState([]);
  const [tab, setTab] = useState("kanban");
  const [busy, setBusy] = useState(false);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [dragId, setDragId] = useState(null);
  const [shortageDlg, setShortageDlg] = useState(null);
  const persisted = (() => {
    try { return JSON.parse(localStorage.getItem(KANBAN_FILTER_KEY)) || {}; } catch { return {}; }
  })();
  const [fSort, setFSort] = useState(persisted.fSort || "all");
  const [fOverdue, setFOverdue] = useState(!!persisted.fOverdue);
  const [query, setQuery] = useState(persisted.query || "");
  const dragRow = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(KANBAN_FILTER_KEY, JSON.stringify({ fSort, fOverdue, query }));
    } catch { /* ignore quota */ }
  }, [fSort, fOverdue, query]);

  const load = async () => setOrders((await api.get("/production")).data || []);
  useEffect(() => {
    load();
    api.get("/production/capacity").then(({ data }) => setCap(data)).catch(() => {});
    api.get("/production/optimize/last").then(({ data }) => {
      const mc = (data?.material_check || []).filter((m) => m.reach_days != null && m.reach_days <= 5);
      setHints(mc);
    }).catch(() => {});
  }, []);

  const todayISO = iso(new Date());
  const sortName = (o) => o.output_item_name || o.recipe_name || "Sorte";
  const overdue = (o) => o.planned_date && o.planned_date < todayISO && o.status !== "completed";
  const unitLabel = (o) => o.bundle_code ? o.bundle_code : (o.output_item_unit || "Stk");
  const CATS = [
    ["Caramell", /caramell|karamell/i], ["Sea Salt", /sea ?salt|salz/i],
    ["Cookies & Cream", /cookies|cream|schoko/i], ["Biscuit", /biscuit|biscoff/i],
    ["Coffee Caramell", /coffee|kaffee/i], ["Trüffel", /tr(ü|u)ffel|truffle/i],
  ];
  const catOf = (o) => { const n = sortName(o); const c = CATS.find(([, re]) => re.test(n)); return c ? c[0] : "Andere"; };
  const durationH = (o) => {
    const grp = o.output_product_group || "";
    const thr = (cap?.throughput && cap.throughput[grp]) || cap?.default_throughput || 100;
    const qty = Number(o.actual_quantity || o.planned_quantity || 0);
    return thr > 0 ? Math.max(qty / thr, 0.15) : 0.5;
  };

  const active = useMemo(() => orders.filter((o) => colOf(o)), [orders]);
  const overdueCount = active.filter(overdue).length;
  const catList = CATS.map(([l]) => l).filter((l) => active.some((o) => catOf(o) === l))
    .concat(active.some((o) => catOf(o) === "Andere") ? ["Andere"] : []);
  const chipCls = (a) => `inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${a ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]" : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)]"}`;
  const jumpTo = (w) => { if (w) setWeekStart((prev) => addDays(prev, w * 7)); setTab("gantt"); };

  /* ---------- Kanban-Übergänge (bestehende Endpoints) ---------- */
  const step = async (o, toKey, force = false) => {
    const from = COL_KEYS.indexOf(colOf(o));
    const to = COL_KEYS.indexOf(toKey);
    if (to <= from) return; // nur vorwärts
    setBusy(true);
    try {
      for (let i = from; i < to; i++) {
        const target = COL_KEYS[i + 1];
        if (target === "in_progress") {
          await api.post(`/production/${o.id}/start`, force ? { force: true } : {});
        } else if (target === "fertig") {
          await api.post(`/production/${o.id}/complete`, {});
        } else if (target === "versandbereit") {
          await api.post(`/production/${o.id}/mark-shipready`);
        }
      }
      toast.success(`${o.number} → ${COLS.find((c) => c.key === toKey).title}`);
      setShortageDlg(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "shortage") {
        setShortageDlg({ order: o, toKey, shortages: d.shortages || [] });
      } else {
        toast.error(typeof d === "string" ? d : `Verschieben fehlgeschlagen (${o.number})`);
      }
    } finally { setBusy(false); }
  };

  /* ---------- Wochenplan (Gantt) ---------- */
  const days = [0, 1, 2, 3, 4].map((i) => addDays(weekStart, i));
  const dayH = Number(cap?.hours_per_day || 8) || 8;
  const totalH = dayH * 5;
  const todayIdx = days.findIndex((d) => iso(d) === todayISO);

  // Reihenfolge lokal halten (nach planned_date, dann Nummer)
  const [seq, setSeq] = useState([]);
  useEffect(() => {
    const list = active.filter((o) => o.status === "planned" || o.status === "in_progress");
    list.sort((a, b) => (a.planned_date || "9999").localeCompare(b.planned_date || "9999") || (a.number || "").localeCompare(b.number || ""));
    setSeq(list.map((o) => o.id));
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => Object.fromEntries(orders.map((o) => [o.id, o])), [orders]);
  const baseMonday = useMemo(() => mondayOf(new Date()), []);
  const weekOffset = Math.round((weekStart - baseMonday) / (7 * 86400000));
  const schedule = useMemo(() => {
    const winStart = weekOffset * totalH;
    const winEnd = winStart + totalH;
    let cum = 0;
    const out = [];
    for (const id of seq) {
      const o = byId[id]; if (!o) continue;
      const dur = durationH(o);
      const startH = cum; const endH = cum + dur; cum = endH;
      if (endH <= winStart || startH >= winEnd) continue; // liegt nicht in dieser Woche
      const relStart = startH - winStart;
      const relEnd = endH - winStart;
      const startsBefore = relStart < 0;      // läuft aus Vorwoche hinein
      const extendsBeyond = relEnd > totalH;  // läuft in Folgewoche weiter
      const clampStart = Math.max(0, relStart);
      const clampEnd = Math.min(totalH, relEnd);
      const dayIndex = Math.max(0, Math.min(4, Math.floor(clampStart / dayH)));
      out.push({
        o, dur, startsBefore, extendsBeyond,
        left: (clampStart / totalH) * 100,
        width: Math.max(2, ((clampEnd - clampStart) / totalH) * 100),
        startDate: iso(addDays(weekStart, dayIndex)),
      });
    }
    return out;
  }, [seq, byId, cap, weekStart, weekOffset, totalH, dayH]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowLinePct = useMemo(() => {
    if (todayIdx < 0) return null;
    const now = new Date();
    const h = Math.max(0, Math.min(dayH, now.getHours() + now.getMinutes() / 60 - START_HOUR));
    return ((todayIdx * dayH + h) / totalH) * 100;
  }, [todayIdx, dayH, totalH]);

  const onDropRow = async (targetId) => {
    if (!dragRow.current || dragRow.current === targetId) return;
    const from = seq.indexOf(dragRow.current);
    const to = seq.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...seq];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setSeq(next);
    dragRow.current = null;
    // Neue Startdaten berechnen & speichern (planned_date)
    let cum = 0;
    const updates = [];
    for (const id of next) {
      const o = byId[id]; if (!o) continue;
      const dayIndex = Math.min(Math.floor(cum / dayH), 4);
      cum += durationH(o);
      const nd = iso(addDays(weekStart, dayIndex));
      if (nd !== (o.planned_date || "")) updates.push({ id, planned_date: nd });
    }
    try {
      await Promise.all(updates.map((u) => api.patch(`/production/${u.id}`, { planned_date: u.planned_date })));
      if (updates.length) { toast.success("Reihenfolge & Termine aktualisiert"); await load(); }
    } catch { toast.error("Termine konnten nicht gespeichert werden"); }
  };

  return (
    <div data-testid="page-production-board">
      <PageHeader
        eyebrow="Produktion"
        title="Aufträge & Wochenplan"
        description="Kanban-Board und Wochenplan aus den bestehenden Produktionsaufträgen."
        actions={
          <div className="flex items-center gap-2">
            {overdueCount > 0 && (
              <span data-testid="overdue-counter" className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300 px-3 h-9 text-sm font-bold">
                <AlertTriangle size={14} /> {overdueCount} überfällig
              </span>
            )}
            <button onClick={() => navigate("/produktion/monitor")} data-testid="btn-goto-monitor"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] px-4 h-9 text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/5">
              <Factory size={15} /> Monitor
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-5" data-testid="board-tabs">
        {[["kanban", "Aufträge (Kanban)", LayoutGrid], ["gantt", "Wochenplan (Gantt)", CalendarRange]].map(([k, label, Ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`inline-flex items-center gap-2 rounded-full px-5 h-10 text-sm font-bold transition-colors ${tab === k ? "bg-[var(--pop-pink)] text-white" : "border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)]"}`}>
            <Ic size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "kanban" && (
        <>
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="kanban-filters">
          <button onClick={() => setFSort("all")} data-testid="kfilter-all" className={chipCls(fSort === "all")}>Alle Sorten</button>
          {catList.map((c) => (
            <button key={c} onClick={() => setFSort(c)} data-testid={`kfilter-${c}`} className={chipCls(fSort === c)}>
              <span className="h-2 w-2 rounded-full" style={{ background: sortColor(c) }} /> {c}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[var(--border)]" />
          <button onClick={() => setFOverdue((v) => !v)} data-testid="kfilter-overdue"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${fOverdue ? "bg-red-600 text-white border-red-600" : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)]"}`}>
            <AlertTriangle size={13} /> Nur überfällig
          </button>
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="kanban-search"
              placeholder="Auftragsnr. oder Sorte suchen…"
              className="h-8 w-52 sm:w-64 rounded-full border border-[var(--border)] bg-[var(--surface)] pl-8 pr-8 text-xs text-[var(--text)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--pop-pink)]"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="kanban-search-clear"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text)]">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x" data-testid="kanban">
          {COLS.map((col) => {
            const q = query.trim().toLowerCase();
            const cards = active.filter((o) => colOf(o) === col.key
              && (fSort === "all" || catOf(o) === fSort)
              && (!fOverdue || overdue(o))
              && (!q || String(o.number || "").toLowerCase().includes(q) || sortName(o).toLowerCase().includes(q)));
            const Ic = col.icon;
            return (
              <div key={col.key} data-testid={`kanban-col-${col.key}`}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); const o = byId[id]; if (o) step(o, col.key); setDragId(null); }}
                   className="shrink-0 w-[160px] sm:w-[280px] snap-start rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-[var(--border)] p-2.5">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="inline-flex items-center gap-1.5 font-display font-bold text-sm text-[var(--text)]"><Ic size={15} className="text-[var(--text-muted)]" /> {col.title}</span>
                  <span className="text-xs font-mono text-[var(--text-muted)] rounded-full bg-[var(--surface)] px-2 py-0.5 border border-[var(--border)]">{cards.length}</span>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {cards.map((o) => {
                    const od = overdue(o);
                    const fromIdx = COL_KEYS.indexOf(col.key);
                    const nextKey = COL_KEYS[fromIdx + 1];
                    return (
                      <div key={o.id} data-testid={`kanban-card-${o.number}`}
                           draggable
                           onDragStart={(e) => { e.dataTransfer.setData("text/plain", o.id); setDragId(o.id); }}
                           onDragEnd={() => setDragId(null)}
                           className={`rounded-xl bg-[var(--surface)] border p-3 cursor-grab active:cursor-grabbing transition-shadow hover:shadow-sm ${od ? "border-red-500/70 ring-1 ring-red-500/40" : "border-[var(--border)]"} ${dragId === o.id ? "opacity-50" : ""}`}>
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: sortColor(sortName(o)) }} />
                          <span className="font-semibold text-sm text-[var(--text)] truncate flex-1">{sortName(o)}</span>
                        </div>
                        <div className="mt-1.5 font-mono font-black text-lg leading-none text-[var(--text)]">{nf(o.actual_quantity || o.planned_quantity)} <span className="text-xs font-semibold text-[var(--text-muted)]">{unitLabel(o)}</span></div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-[11px] text-[var(--text-muted)] font-mono">{o.number}</span>
                          {od
                            ? <span className="text-[11px] font-bold text-red-600">Überfällig</span>
                            : <span className="text-[11px] text-[var(--text-muted)]">{o.planned_date || "—"}</span>}
                        </div>
                        {nextKey && (
                          <button data-testid={`kanban-next-${o.number}`} disabled={busy}
                                  onClick={() => step(o, nextKey)}
                                  className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-lg bg-[var(--pop-pink)] text-white h-8 text-xs font-bold hover:opacity-90 disabled:opacity-50">
                            {COLS[fromIdx + 1].title} <ArrowRight size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {cards.length === 0 && <div className="text-[11px] text-[var(--text-muted)] italic px-1 py-3">Keine Aufträge</div>}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {tab === "gantt" && (
        <div data-testid="gantt">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="week-prev" onClick={() => setWeekStart((w) => addDays(w, -7))}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 h-9 text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/5"><ChevronLeft size={16} /> Vorige</button>
            <div className="font-display font-bold text-[var(--text)]">KW {fmtDay(days[0])} – {fmtDay(days[4])}</div>
            <button data-testid="week-next" onClick={() => setWeekStart((w) => addDays(w, 7))}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 h-9 text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/5">Nächste <ChevronRight size={16} /></button>
          </div>

          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
            {/* Kopf: Wochentage */}
            <div className="flex border-b border-[var(--border)]">
              <div className="w-40 shrink-0 p-2 text-xs font-semibold text-[var(--text-muted)]">Sorte / Auftrag</div>
              <div className="relative flex-1 grid grid-cols-5">
                {days.map((d, i) => (
                  <div key={i} className={`p-2 text-center text-xs font-semibold border-l border-[var(--border)] ${i === todayIdx ? "bg-blue-500/10 text-blue-600 dark:text-blue-300" : "text-[var(--text-muted)]"}`}>
                    {fmtDay(d)}{i === todayIdx ? " · Heute" : ""}
                  </div>
                ))}
              </div>
            </div>

            {/* Zeilen: je Auftrag */}
            <div className="relative">
              {/* Heute-Spalte + aktuelle Uhrzeit-Linie über alle Zeilen (nur Timeline-Bereich) */}
              {(todayIdx >= 0 || nowLinePct != null) && (
                <div className="absolute top-0 bottom-0 pointer-events-none z-10" style={{ left: "10rem", right: 0 }}>
                  {todayIdx >= 0 && <div className="absolute top-0 bottom-0 bg-blue-500/5" style={{ left: `${(todayIdx / 5) * 100}%`, width: `${100 / 5}%` }} />}
                  {nowLinePct != null && <div className="absolute top-0 bottom-0 w-0.5 bg-blue-500" style={{ left: `${nowLinePct}%` }} />}
                </div>
              )}
              {schedule.length === 0 && <div className="p-8 text-center text-sm text-[var(--text-muted)]">Keine offenen/laufenden Aufträge in dieser Woche.</div>}
              {schedule.map(({ o, dur, left, width, startDate, startsBefore, extendsBeyond }) => {
                const od = overdue(o);
                return (
                  <div key={o.id} data-testid={`gantt-row-${o.number}`}
                       draggable
                       onDragStart={() => { dragRow.current = o.id; }}
                       onDragOver={(e) => e.preventDefault()}
                       onDrop={() => onDropRow(o.id)}
                       className="flex items-center border-b border-[var(--border)] last:border-b-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] cursor-grab active:cursor-grabbing">
                    <div className="w-40 shrink-0 p-2 flex items-center gap-2 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: sortColor(sortName(o)) }} />
                      <span className="text-xs font-semibold truncate text-[var(--text)]">{sortName(o)}</span>
                    </div>
                    <div className="relative flex-1 h-11 grid grid-cols-5">
                      {[0, 1, 2, 3, 4].map((i) => <div key={i} className="border-l border-[var(--border)]" />)}
                      <div title={`${sortName(o)} · ${nf(o.actual_quantity || o.planned_quantity)} ${unitLabel(o)} · ${startDate} · ~${dur.toLocaleString("de-DE", { maximumFractionDigits: 1 })} h · ${o.status}`}
                           data-testid={`gantt-bar-${o.number}`}
                           className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-md flex items-center px-2 text-[10px] font-bold text-black/90 truncate ${startsBefore ? "rounded-l-none" : ""} ${extendsBeyond ? "rounded-r-none" : ""} ${od ? "ring-2 ring-red-500" : ""}`}
                           style={{ left: `${left}%`, width: `${width}%`, background: sortColor(sortName(o)) }}>
                        {startsBefore && (
                          <button type="button" data-testid={`gantt-prev-${o.number}`}
                                  onClick={(e) => { e.stopPropagation(); jumpTo(-1); }}
                                  className="mr-1 shrink-0 whitespace-nowrap underline decoration-dotted cursor-pointer">‹ Vorwoche</button>
                        )}
                        {nf(o.actual_quantity || o.planned_quantity)} {unitLabel(o)}
                        {extendsBeyond && (
                          <button type="button" data-testid={`gantt-next-${o.number}`}
                                  onClick={(e) => { e.stopPropagation(); jumpTo(1); }}
                                  className="ml-1 shrink-0 whitespace-nowrap underline decoration-dotted cursor-pointer">→ Folgewoche</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Engpass-Hinweise aus KI-Optimierung */}
          {hints.length > 0 && (
            <div data-testid="gantt-hints" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 p-4">
              <div className="flex items-center gap-2 font-bold text-amber-800 dark:text-amber-300 text-sm mb-2">
                <AlertTriangle size={16} /> Engpässe (KI-Optimierung)
              </div>
              <ul className="space-y-1">
                {hints.map((m, i) => (
                  <li key={i} data-testid={`gantt-hint-${i}`} className="text-sm text-amber-800 dark:text-amber-200">
                    <b>{m.name}</b> reicht noch ~{nf(m.reach_days)} {Number(m.reach_days) === 1 ? "Tag" : "Tage"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Fehlmengen-Bestätigung beim Kanban-Move */}
      <Dialog open={!!shortageDlg} onOpenChange={(o) => { if (!o) setShortageDlg(null); }}>
        <DialogContent data-testid="shortage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Rohstoff-Fehlmenge</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-muted)]">
            Für <b>{shortageDlg?.order?.number}</b> reicht der Bestand einiger Zutaten nicht.
            Möchtest du <b>trotzdem freigeben</b>? Der Bestand wird wie gewohnt gebucht (kann negativ werden).
          </p>
          {shortageDlg?.shortages?.length > 0 && (
            <div className="mt-1 max-h-48 overflow-y-auto space-y-1">
              {shortageDlg.shortages.map((s, i) => (
                <div key={i} data-testid={`shortage-row-${i}`} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-2 py-1.5">
                  <span className="truncate">{s.name}</span>
                  <span className="font-mono text-red-700 dark:text-red-300 shrink-0">−{nf(s.shortage)} {s.unit}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShortageDlg(null)} data-testid="shortage-cancel">Abbrechen</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="shortage-confirm" disabled={busy}
                    onClick={() => { const s = shortageDlg; setShortageDlg(null); if (s) step(s.order, s.toKey, true); }}>
              Freigabe erzwingen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
